import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, DropDownButton } from '@progress/kendo-react-buttons'
import { Label } from '@progress/kendo-react-labels'
import { Checkbox, Input } from '@progress/kendo-react-inputs'
import { Grid, GridCellProps, GridColumn, GridItemChangeEvent } from '@progress/kendo-react-grid'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'
import {
  BranchSelect,
  BuildingUsecaseSelect,
  ErrorMessages,
  ErrorMessage,
  ExpenseSelect,
  FiscalMonthDropDownList,
  DepartmentDropDownList,
  AvailabilityStatusSelect,
  FiscalYearDropDownList,
  ObtainHistorySelect,
  ConstrFormSelect,
  EvaluationSelect,
  EffortLevelSelect,
  ProcurementStatusSelect,
  NormalStatusSelect,
  ConstrTypeSelect,
  StructureTypeSelect,
  FromToNumberInput,
  GeneralPurposeSearch,
  ConfirmDialog,
  OutputYearsDropDownList
} from '../../../components/parts'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { Authority } from '../../../gateway/system/Authority'
import { useAppSelector, useAppDispatch, DataExportConditionActions, DataExportInitialState, CommonActions } from '../../../stores'
import { DataExportReq, GetDigestiveSimulationFileUrlReqParams, GetLaborStackManagementTableFileUrlsReqParams, GetSimulationExportReqParams, PutConditionReq, ReportOutputReq } from '@gateway.Simulation'
import { GetConditionGateway } from '../../../gateway/simulation/GetConditionGateway'
import { PutConditionGateway } from '../../../gateway/simulation/PutConditionGateway'
import { SyoukatakaGateWay, YamadumiManagementGateWay, SimulationExportGateway, DataExportGateway } from '../../../gateway/simulation'
import { BlobFileService, MessageProvider } from '../../../services'
import './StatusList.css'
import { SortMethodDropDownList } from '../../../components/parts/SortMethodDropDownList'
import Env from '../../../Env'
import { FaceRecognitionDataExportGateway } from '../../../gateway/simulation/FaceRecognitionDataExportGateway'

export const StatusList: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch)
  usePageTitle('山積状況一覧')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const userProp = useAppSelector((state) => state.common.userProp)
  const initialData = useAppSelector((state) => state.common.initialData)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)
  const dataExportConditionStore = useAppSelector((state) => state.dataExport.dataExportCondition)
  const searchCondition = dataExportConditionStore.searchCondition
  const generalPurposeSearchList = dataExportConditionStore.generalPurposeSearchList
  const outputItemList = dataExportConditionStore.outputItemList

  const [initialClearDone, setInitialClearDone] = useState(false)
  const [myCondition, setMyCondition] = useState<PutConditionReq>({ conditionKbn: '01', conditionCd: '1', userId: '', condition: '', updDtm: null })
  const [isAllSelectMode, setIsAllSelectMode] = useState(true)
  const [confirmDialogShow, setConfirmDialogShow] = useState(false)
  const [messageId, setMessageId] = useState('')
  const [messageArgslist, setMessageArgslist] = useState([''])
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const [faceRecognitionCsvDataOutputMode, setFaceRecognitionCsvDataOutputMode] = useState(0)
  /**
   * 検索条件の変更（Storeの値を再登録）
   * @param prop 変更するプロパティ名
   * @param value 変更するプロパティ値
   */
  const handleSearchCondition = (prop: string, value: string | string[] | number | number[] | null) => {
    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition: {
          ...searchCondition, [prop]: value
        },
        generalPurposeSearchList,
        outputItemList,
        displayStartYear
      })
    )
  }

  /**
   * 汎用検索の変更（Storeの値を再登録）
   * @param index 変更する汎用検索のインデックス番号
   * @param prop 変更するプロパティ名
   * @param value 変更するプロパティ値
   */
  const handleGeneralPurposeSearch = (index: number, prop: string, value: string) => {
    const newGeneralPurposeSearchList = [...generalPurposeSearchList]
    newGeneralPurposeSearchList[index] = {
      ...newGeneralPurposeSearchList[index],
      [prop]: value,
      searchItem: prop === 'searchItemCd' ? initialData.codeNames('KE03').find(cn => cn.CODE === value)?.TXT1 ?? null : newGeneralPurposeSearchList[index].searchItem,
      searchMethod: prop === 'searchMethodCd' ? initialData.codeNames('KE04').find(cn => cn.CODE === value)?.NUM1 ?? null : newGeneralPurposeSearchList[index].searchMethod
    }

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition,
        generalPurposeSearchList: newGeneralPurposeSearchList,
        outputItemList,
        displayStartYear
      })
    )
  }

  /**
   * 出力項目の変更（Storeの値を再登録）
   * @param index 変更する出力項目のインデックス番号
   * @param prop 変更するプロパティ名
   * @param value 変更するプロパティ値
   */
  const handleOutputItemList = (index: number, prop: string, value: string | number | boolean | null) => {
    const newOutputItemList = [...outputItemList]
    newOutputItemList[index] = {
      ...newOutputItemList[index], [prop]: value
    }

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition,
        generalPurposeSearchList,
        outputItemList: newOutputItemList,
        displayStartYear
      })
    )
  }

  /**
   * DataGridを経由した出力項目の変更（Storeの値を再登録）
   * 主にテキスト（数値）系の項目で使用
   * @param e 変更するDataGridItem
   */
  const handleOutputItemData = (e: GridItemChangeEvent) => {
    const field = e.field ?? ''
    let value = e.value ?? ''

    // 数値入力のみ許可
    if (e.field === 'outputOrder' || e.field === 'sortOrder') {
      if (isNullOrEmpty(value)) {
        value = null
      } else {
        if (value.match(/^\d+$/) !== null) {
          value = Number(value)
        } else {
          return
        }
      }
    }

    const changeData = outputItemList.map((item) =>
      item === e.dataItem ? { ...item, [field]: value } : item
    )

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition,
        generalPurposeSearchList,
        outputItemList: changeData,
        displayStartYear
      })
    )
  }

  /**
   * 全選択／全解除ボタンのイベントハンドラ
   * （出力対象チェックボックスの全選択／全解除を行う）
   */
  const handleSelect = () => {
    const newOutputItemList = outputItemList.map(oi => {
      return { ...oi, outputCheck: isAllSelectMode }
    })

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition,
        generalPurposeSearchList,
        outputItemList: newOutputItemList,
        displayStartYear
      })
    )

    setIsAllSelectMode(!isAllSelectMode)
  }

  /**
   * 並べ替えボタンのイベントハンドラ
   * （出力項目をCSV出力順の小さい順に並び替える）
   */
  const handleItemSort = () => {
    const newOutputItemList = [...outputItemList]
    newOutputItemList.sort((a, b) => (a.outputOrder ?? Number.MAX_SAFE_INTEGER) - (b.outputOrder ?? Number.MAX_SAFE_INTEGER))

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition,
        generalPurposeSearchList,
        outputItemList: newOutputItemList,
        displayStartYear
      })
    )
  }

  /**
   * クリアボタンのイベントハンドラ
   * （検索条件、出力項目を初期値の状態に戻す）
   */
  const handleClear = () => {
    const codeNames = initialData.codeNames('KE03')
    const initOutputItemList = codeNames.map(cn => {
      let annotation = ''
      if (cn.TXT2 !== null && cn.TXT2 !== undefined) {
        const annotationCodes = initialData.codeNames(cn.TXT2)
        annotation = `（${annotationCodes.map(ac => `${ac.CODE ?? ''}:${ac.NAME ?? ''}`).join(', ')}）`
      }
      return {
        inEdit: true,
        outputItemCd: cn.CODE ?? null,
        outputItemNm: cn.TXT1 ?? null,
        outputItemDispNm: cn.NAME ?? '',
        outputItemDispNmWithAnnotation: `${cn.NAME ?? ''}${annotation}`,
        outputCheck: cn.NUM2 === 1,
        outputOrder: cn.NUM1 ?? null,
        sortOrder: null,
        sortMethod: ''
      }
    })

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition: DataExportInitialState.dataExportCondition.searchCondition,
        generalPurposeSearchList: DataExportInitialState.dataExportCondition.generalPurposeSearchList,
        outputItemList: initOutputItemList,
        displayStartYear
      })
    )
  }

  /**
   * 条件登録ボタンのイベントハンドラ
   * （検索条件、出力項目の設定状態をDBに保存する）
   */
  const handleRegistCondition = () => {
    // 検索条件の設定不備チェック
    if (YearAndMonthCheck() || generalPurposeSearchCheck()) {
      return
    }

    const reqParams = { ...myCondition, userId: userProp?.userId ?? '', condition: JSON.stringify(dataExportConditionStore) }
    PutConditionGateway.putCondition(reqParams)
      .then((res) => {
        if (!res.error) {
          // 次の更新に向け最新の更新日時を取得する
          GetConditionGateway.getCondition({ conditionKbn: myCondition.conditionKbn, conditionCd: myCondition.conditionCd, userId: userProp?.userId ?? '' })
            .then((res) => {
              if (!res.error) {
                if (res.data.condition !== null) {
                  setMyCondition({ ...myCondition, condition: res.data.condition.condition, updDtm: res.data.condition.updDtm })
                }
              } else {
                if (Array.isArray(res.messages) && res.messages.length > 0) {
                  const errorMessages = res.messages.map<ErrorMessage>((message) => {
                    return {
                      id: message.id,
                      content: message.message
                    }
                  })
                  setErrorMessages(errorMessages)
                }
              }
            })
            .finally(() => {})

          // Notificationを表示する
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '検索条件登録')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }

  /**
   * 検索条件の取得
   */
  const handleGetCondition = () => {
    // 画面描画の初期化処理が終わっていない場合は実施しない
    if (!initialClearDone) {
      return
    }

    const reqParams = { conditionKbn: myCondition.conditionKbn, conditionCd: myCondition.conditionCd, userId: userProp?.userId ?? '' }
    GetConditionGateway.getCondition(reqParams)
      .then((res) => {
        if (!res.error) {
          if (res.data.condition !== null) {
            const condition: DataExportReq = JSON.parse(res.data.condition.condition)
            mergeCondition(condition)
            setMyCondition({ ...myCondition, condition: res.data.condition.condition, updDtm: res.data.condition.updDtm })
          }
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }

  /**
   * 条件取込ボタンのイベントハンドラ
   * （検索条件、出力項目の設定状態をファイルから取り込む）
   */
  const handleConditionImport = async () => {
    const options: OpenFilePickerOptions = {
      excludeAcceptAllOption: true,
      types: [
        {
          description: 'Condition Text',
          accept: {
            'text/plain': ['.txt', '.text']
          }
        }
      ]
    }
    try {
      const fhList = await window.showOpenFilePicker(options)
      const fh = fhList[0]
      const file = await fh.getFile()
      const reader = new FileReader()
      reader.readAsText(file, 'UTF-8')
      reader.onload = (event) => {
        if (event.target !== null) {
          try {
            const condition = JSON.parse(event.target.result as string)
            mergeCondition(condition)
          } catch (error) {
            setErrorMessages([
              {
                id: 'E003',
                content: MessageProvider.provide('E003', '条件ファイルの取込')
              }
            ])
          }
        }
      }
    } catch {
      // showOpenFilePicker をキャンセルで閉じたときにエラーが発生する仕様のため
      // そのエラーを補足して破棄する
      // 上記の理由のため、この catch ブロックでは特に処理は行わない
    }
  }

  /**
   * 条件出力ボタンのイベントハンドラ
   * （検索条件、出力項目の設定状態をファイルに出力する）
   */
  const handleConditionExport = async () => {
    // 検索条件の設定不備チェック
    if (YearAndMonthCheck() || generalPurposeSearchCheck()) {
      return
    }

    const options: SaveFilePickerOptions = {
      suggestedName: '条件.txt',
      excludeAcceptAllOption: true,
      types: [
        {
          description: 'Condition Text',
          accept: {
            'text/plain': ['.txt', '.text']
          }
        }
      ]
    }
    try {
      const fh = await window.showSaveFilePicker(options)
      const stream = await fh.createWritable()
      const blob = new Blob([JSON.stringify(dataExportConditionStore)], { type: 'text/plain' })
      await stream.write(blob)
      await stream.close()
    } catch {
      // showSaveFilePicker をキャンセルで閉じたときにエラーが発生する仕様のため
      // そのエラーを補足して破棄する
      // 上記の理由のため、この catch ブロックでは特に処理は行わない
    }
  }

  /**
   * 山積シミュレーション出力ボタンのイベントハンドラ
   * （帳票出力の確認ダイアログを表示）
   */
  const handleYamadumiSimulationOutput = () => {
    setMessageId('I002')
    setMessageArgslist(['山積シミュレーション', '出力開始'])
    setConfirmDialogShow(true)
  }

  /**
   * 消化高シミュレーション出力ボタンのイベントハンドラ
   * （帳票出力の確認ダイアログを表示）
   */
  const handleSyoukatakaSimulationOutput = () => {
    setMessageId('I002')
    setMessageArgslist(['消化高シミュレーション', '出力開始'])
    setConfirmDialogShow(true)
  }

  /**
   * 山積管理表出力ボタンのイベントハンドラ
   * （帳票出力の確認ダイアログを表示）
   */
  const handleYamadumiManagementOutput = () => {
    setMessageId('I002')
    setMessageArgslist(['山積管理表', '出力開始'])
    setConfirmDialogShow(true)
  }

  /**
   * 消化高・労務集計グラフボタンのイベントハンドラ
   */
  const handleTableauGraph = () => {
    const expenseCodeNames = initialData.codeNames('HI01')
    const evaluationValueCodeNames = initialData.codeNames('HY01')

    // クエリパラメータの生成
    let targetYearMonth: string | null = null
    let expense: string | null = null
    let evaluationValueList: string[] | null = null

    let params: string = ''

    // 今回シミュレーション年月
    if (searchCondition.reportTargetYear !== null && searchCondition.reportTargetMonth !== null) {
      targetYearMonth = `${searchCondition.reportTargetYear}${searchCondition.reportTargetMonth}`
      if (params.length > 0) {
        params = params + '&'
      }
      params = params + `今回シミュレーション年月=${targetYearMonth}`
    }

    // 前回シミュレーション年月
    if (targetYearMonth !== null) {
      const curYear = targetYearMonth.substring(0, 4)
      const curMonth = targetYearMonth.substring(4, 6)
      const preYear = curMonth === '01' ? (Number(curYear) - 1).toString() : curYear
      const preMonth = curMonth === '01' ? '12' : (Number(curMonth) - 1).toString().padStart(2, '0')
      const preYearMonth = `${preYear}${preMonth}`
      params = params + `&前回シミュレーション年月=${preYearMonth}`
    }

    // 費目
    if (searchCondition.expense !== null && searchCondition.expense.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      expense = expenseCodeNames.find(exp => exp.CODE === searchCondition.expense![0])?.NAME ?? ''
      if (params.length > 0) {
        params = params + '&'
      }
      params = params + `費目名=${expense}`
    }

    // 評価値
    if (searchCondition.evaluationValueCd !== null && searchCondition.evaluationValueCd.length > 0) {
      evaluationValueList = searchCondition.evaluationValueCd.map(ecd => evaluationValueCodeNames.find(e => e.CODE === ecd)?.NAME ?? '')
      if (params.length > 0) {
        params = params + '&'
      }
      params = params + `評価値=${evaluationValueList.join(',')}`
    }

    window.open(`${Env.TABLEAU_URL}?${params}`, '_blank')
  }

  /**
   * 業者山積グラフボタンのイベントハンドラ
   */
  const handleTableauGraphSupplier = () => {
    const branchCodeNames = initialData.codeNames('SI01')
    const expenseCodeNames = initialData.codeNames('HI01')

    let params: string = ''

    // シミュレーション年月
    if (params.length > 0) {
      params = params + '&'
    }
    if (searchCondition.reportTargetYear !== null && searchCondition.reportTargetMonth !== null) {
      const targetYearMonth = `${searchCondition.reportTargetYear}${searchCondition.reportTargetMonth}`
      params = params + `SIMULATION_YM=${targetYearMonth}`
    } else {
      params = params + 'SIMULATION_YM='
    }

    // 業者名
    if (params.length > 0) {
      params = params + '&'
    }
    params = params + 'SUPPLIER_NM=' // 業者名フィルタの初期化のためにブランクを指定する

    // 支店
    if (params.length > 0) {
      params = params + '&'
    }
    if (searchCondition.branchCd !== null && searchCondition.branchCd.length > 0) {
      const branchCode = searchCondition.branchCd[0]
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const branchName = branchCodeNames.find(br => br.CODE === searchCondition.branchCd![0])?.NAME ?? ''
      params = params + `支店=${branchCode}:+${branchName}`
    } else {
      params = params + '支店='
    }

    // 費目
    if (params.length > 0) {
      params = params + '&'
    }
    if (searchCondition.expense !== null && searchCondition.expense.length > 0) {
      const expenseCode = searchCondition.expense[0]
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const expenseName = expenseCodeNames.find(exp => exp.CODE === searchCondition.expense![0])?.NAME ?? ''
      params = params + `費目=${expenseCode}:+${expenseName}`
    } else {
      params = params + '費目='
    }

    window.open(`${Env.TABLEAU_SUPPLIER_URL}?${params}`, '_blank')
  }

  /**
   * CSV出力ボタンのイベントハンドラ
   * （CSV出力の確認ダイアログを表示）
   */
  const handleCsvDataOutput = () => {
    setMessageId('I002')
    setMessageArgslist(['CSV出力', '出力開始'])
    setConfirmDialogShow(true)
  }

  /**
   * 顔認証出力ボタンのイベントハンドラ
   * （顔認証出力の確認ダイアログを表示）
   */
  const handleFaceRecognitionCsvDataOutput = (index: number) => {
    setFaceRecognitionCsvDataOutputMode(index)
    setMessageId('I002')
    setMessageArgslist(['顔認証出力', '出力開始'])
    setConfirmDialogShow(true)
  }

  /**
   * 確認ダイアログで「OK」が押下された場合のイベントハンドラ
   */
  const dialogComfirmOkBtn = () => {
    setConfirmDialogShow(false)
    if (messageArgslist[0] === '山積シミュレーション') {
      if (reportOutputCheck() || YearAndMonthCheck()) {
        return
      }
      dispatch(CommonActions.setIsLoading(true))
      const reqParams: GetSimulationExportReqParams & ReportOutputReq = {
        advancedSearchFlg: 1,
        ...searchCondition,
        expense: searchCondition.expense ?? [],
        branchCd: searchCondition.branchCd ?? [],
        targetYear: searchCondition.reportTargetYear ?? '',
        targetMonth: searchCondition.reportTargetMonth ?? '',
        format: searchCondition.reportFormat ?? '',
        displayStartYear
      }
      SimulationExportGateway.getSimulationExport(reqParams)
        .then((res) => {
          if (!res.error) {
            if (typeof res.data.fileUrl === 'string') {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', '山積シミュレーション出力')
                }
              ])
              BlobFileService.downloadRedirectTo(res.data.fileUrl)
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '消化高シミュレーション') {
      if (reportOutputCheck() || YearAndMonthCheck()) {
        return
      }
      dispatch(CommonActions.setIsLoading(true))
      const reqParams: GetDigestiveSimulationFileUrlReqParams & ReportOutputReq = {
        advancedSearchFlg: 1,
        ...searchCondition,
        expense: searchCondition.expense ?? [],
        branchCd: searchCondition.branchCd ?? [],
        targetYear: searchCondition.reportTargetYear ?? '',
        targetMonth: searchCondition.reportTargetMonth ?? '',
        compareBranchCd: searchCondition.compareBranchCd ?? '',
        displayStartYear
      }
      SyoukatakaGateWay.getDigestiveSimulationFileUrl(reqParams)
        .then((res) => {
          if (!res.error) {
            if (typeof res.data.fileUrl === 'string') {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', '消化高シミュレーション出力')
                }
              ])
              BlobFileService.downloadRedirectTo(res.data.fileUrl)
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '山積管理表') {
      if (reportOutputCheck() || YearAndMonthCheck()) {
        return
      }
      dispatch(CommonActions.setIsLoading(true))
      searchCondition.expense?.forEach(expense => {
        const reqParams: GetLaborStackManagementTableFileUrlsReqParams & ReportOutputReq = {
          advancedSearchFlg: 1,
          ...searchCondition,
          expenses: searchCondition.expense ?? [],
          branchCd: searchCondition.branchCd ?? [],
          targetYear: searchCondition.reportTargetYear ?? '',
          targetMonth: searchCondition.reportTargetMonth ?? '',
          format: searchCondition.reportFormat ?? '',
          outputYears: searchCondition.reportOutputYears ?? '',
          displayStartYear
        }
        YamadumiManagementGateWay.getLaborStackManagementTableFileUrls(reqParams)
          .then((res) => {
            if (!res.error) {
              if (Array.isArray(res.data.workingFileNameList)) {
                setNotificationMessages([
                  {
                    id: 'I004',
                    content: MessageProvider.provide('I004', '山積管理表出力')
                  }
                ])
                res.data.workingFileNameList.forEach((filePath) => {
                  if (typeof filePath === 'string') {
                    BlobFileService.downloadRedirectTo(filePath)
                  }
                })
              }
            } else {
              if (Array.isArray(res.messages) && res.messages.length > 0) {
                const apiMessages = res.messages.map<ErrorMessage>((message) => {
                  return {
                    id: message.id,
                    content: message.message
                  }
                })
                setErrorMessages(apiMessages)
              }
            }
          })
          .finally(() => {
            dispatch(CommonActions.setIsLoading(false))
          })
      })
    } else if (messageArgslist[0] === 'CSV出力') {
      if (YearAndMonthCheck() || generalPurposeSearchCheck()) {
        return
      }
      dispatch(CommonActions.setIsLoading(true))
      DataExportGateway.postDataExport(dataExportConditionStore, displayStartYear)
        .then((res) => {
          if (!res.error) {
            if (typeof res.data.fileUrl === 'string') {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', 'CSV出力')
                }
              ])
              BlobFileService.downloadRedirectTo(res.data.fileUrl)
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '顔認証出力') {
      if (YearAndMonthCheck()) {
        return
      }
      dispatch(CommonActions.setIsLoading(true))
      FaceRecognitionDataExportGateway.postDataExport(dataExportConditionStore, faceRecognitionCsvDataOutputMode)
        .then((res) => {
          if (!res.error) {
            if (typeof res.data.fileUrl === 'string') {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', '顔認証CSV出力')
                }
              ])
              BlobFileService.downloadRedirectTo(res.data.fileUrl)
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
  }

  /**
   * 帳票出力時のチェック処理
   * @returns true: エラー有り、false: エラー無し
   */
  const reportOutputCheck = () => {
    // 必須項目チェック
    const requiredItem = []
    if (searchCondition.reportTargetYear === null) {
      requiredItem.push('帳票出力年度')
    }
    if (searchCondition.reportTargetMonth === null) {
      requiredItem.push('帳票出力月度')
    }
    if (searchCondition.branchCd === null || searchCondition.branchCd.length === 0) {
      requiredItem.push('支店')
    }
    if (searchCondition.expense === null || searchCondition.expense.length === 0) {
      requiredItem.push('費目')
    }
    if (messageArgslist[0] === '山積シミュレーション' || messageArgslist[0] === '山積管理表') {
      if (searchCondition.reportFormat === null) {
        requiredItem.push('帳票フォーマット')
      }
    }
    if (messageArgslist[0] === '山積管理表') {
      if (searchCondition.reportOutputYears === null) {
        requiredItem.push('出力年数')
      }
    }

    if (requiredItem.length !== 0) {
      setErrorMessages([
        {
          id: 'E001',
          content: MessageProvider.provide('E001', requiredItem.join(','), '帳票の出力')
        }
      ])
      return true
    }

    // 年月度チェック
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    if (
      Number(searchCondition.reportTargetYear) > currentYear ||
      (Number(searchCondition.reportTargetYear) === currentYear && Number(searchCondition.reportTargetMonth) > currentMonth)
    ) {
      setErrorMessages([
        {
          id: 'E014',
          content: MessageProvider.provide('E014', '帳票出力年月度', '当月度以前')
        }
      ])
      return true
    }

    return false
  }

  /**
   * 年月度の片側指定チェック処理
   * @returns true: エラー有り、false: エラー無し
   */
  const YearAndMonthCheck = () => {
    if ((!isNullOrEmpty(searchCondition.exportTargetYearFrom) && isNullOrEmpty(searchCondition.exportTargetMonthFrom)) ||
      (isNullOrEmpty(searchCondition.exportTargetYearFrom) && !isNullOrEmpty(searchCondition.exportTargetMonthFrom))
    ) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', 'データ出力期間（開始）年とデータ出力期間（開始）月は両方に値')
        }
      ])
      return true
    }
    if ((!isNullOrEmpty(searchCondition.exportTargetYearTo) && isNullOrEmpty(searchCondition.exportTargetMonthTo)) ||
      (isNullOrEmpty(searchCondition.exportTargetYearTo) && !isNullOrEmpty(searchCondition.exportTargetMonthTo))
    ) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', 'データ出力期間（終了）年とデータ出力期間（終了）月は両方に値')
        }
      ])
      return true
    }
    if ((!isNullOrEmpty(searchCondition.startY) && isNullOrEmpty(searchCondition.startM)) ||
      (isNullOrEmpty(searchCondition.startY) && !isNullOrEmpty(searchCondition.startM))
    ) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', '工期（開始）年と工期（開始）月は両方に値')
        }
      ])
      return true
    }
    if ((!isNullOrEmpty(searchCondition.completionY) && isNullOrEmpty(searchCondition.completionM)) ||
      (isNullOrEmpty(searchCondition.completionY) && !isNullOrEmpty(searchCondition.completionM))
    ) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', '工期（終了）年と工期（終了）月は両方に値')
        }
      ])
      return true
    }
    if ((!isNullOrEmpty(searchCondition.obtainY) && isNullOrEmpty(searchCondition.obtainM)) ||
      (isNullOrEmpty(searchCondition.obtainY) && !isNullOrEmpty(searchCondition.obtainM))
    ) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', '入手年月年と入手年月月は両方に値')
        }
      ])
      return true
    }

    return false
  }

  /**
   * 汎用検索の不完全指定チェック処理
   * @returns true: エラー有り、false: エラー無し
   */
  const generalPurposeSearchCheck = () => {
    for (let i = 0; i < generalPurposeSearchList.length; i++) {
      const item = generalPurposeSearchList[i]
      if (!(isNullOrEmpty(item.searchItemCd) && isNullOrEmpty(item.searchValue) && isNullOrEmpty(item.searchMethodCd)) &&
        (isNullOrEmpty(item.searchItemCd) || isNullOrEmpty(item.searchValue) || isNullOrEmpty(item.searchMethodCd))) {
        setErrorMessages([
          {
            id: 'E002',
            content: MessageProvider.provide('E002', `汎用検索${i + 1}は全ての値`)
          }
        ])
        return true
      }
    }

    return false
  }

  /**
   * 初期データの検索条件とDBに保存されていた検索条件のマージ処理
   * @param condition DBに保存されていた検索条件（各ユーザの初期検索条件）
   */
  const mergeCondition = (condition: DataExportReq) => {
    // MEMO:
    //   DBに保存されていた検索条件をそのまま初期値として採用してしまうと検索条件や出力項目に追加があった際に消えてしまうため
    //   初期データの検索条件をベースとしてDBに保存されていた検索条件をマージする形とする

    // 汎用検索のマージ
    const mergedGeneralPurposeSearchList = []
    for (let i = 0; i < generalPurposeSearchList.length; i++) {
      if (i < condition.generalPurposeSearchList.length) {
        mergedGeneralPurposeSearchList.push(condition.generalPurposeSearchList[i])
      } else {
        mergedGeneralPurposeSearchList.push(generalPurposeSearchList[i])
      }
    }
    // 出力項目のマージ
    const mergedOutputItemList = []
    for (let i = 0; i < outputItemList.length; i++) {
      const findedOutputItem = condition.outputItemList.find(oi => oi.outputItemCd === outputItemList[i].outputItemCd)
      if (findedOutputItem !== undefined) {
        mergedOutputItemList.push(
          {
            ...outputItemList[i],
            outputCheck: findedOutputItem.outputCheck,
            outputOrder: findedOutputItem.outputOrder,
            sortOrder: findedOutputItem.sortOrder,
            sortMethod: findedOutputItem.sortMethod
          }
        )
      } else {
        mergedOutputItemList.push(outputItemList[i])
      }
    }

    dispatch(
      DataExportConditionActions.setDataExportCondition({
        searchCondition: {
          ...searchCondition,
          ...condition.searchCondition,
          // 帳票出力年月度は保持していた値ではなく、当月の値を設定する
          reportTargetYear: new Date().getFullYear().toString(),
          reportTargetMonth: (new Date().getMonth() + 1).toString().padStart(2, '0'),
          exportTargetYearFrom: new Date().getFullYear().toString(),
          exportTargetMonthFrom: (new Date().getMonth() + 1).toString().padStart(2, '0'),
          exportTargetYearTo: new Date().getFullYear().toString(),
          exportTargetMonthTo: (new Date().getMonth() + 1).toString().padStart(2, '0')
        },
        generalPurposeSearchList: mergedGeneralPurposeSearchList,
        outputItemList: mergedOutputItemList,
        displayStartYear
      })
    )
  }

  /**
   * 値が無効値（null, undefined, 空文字）かどうかを判別する
   * @param value 値
   * @returns true: 無効値、false: 有効値
   */
  const isNullOrEmpty = (value: string | null | undefined) => {
    return value === null || value === undefined || value === ''
  }

  // 初期化処理
  useEffect(() => {
    handleClear()
    setInitialClearDone(true)
  }, [])

  // 各ユーザの初期検索条件の設定
  useEffect(() => {
    handleGetCondition()
  }, [initialClearDone])

  return (
    <div style={{ height: '95%' }}>
      <Card style={{ width: '90%', height: '30%', margin: '30px auto 10px' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <CardTitle>山積状況一覧</CardTitle>
        </CardHeader>
        <div style={{ overflow: 'auto', overflowY: 'scroll' }}>
        <CardBody>
          <GridLayout
            cols={[
              { width: '7%' },
              { width: '8%' },
              { width: '8%' },
              { width: '7%' },
              { width: '8%' },
              { width: '8%' },
              { width: '7%' },
              { width: '8%' },
              { width: '8%' },
              { width: '7%' },
              { width: '8%' },
              { width: '8%' }
            ]}
            gap={ { cols: 10, rows: 5 }}
            style={{ fontSize: '0.9em' }}>
            <GridLayoutItem row={1} col={1}>
              <Label>帳票出力年月度</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2} colSpan={2}>
              <FiscalMonthDropDownList
                popupSettings={{ className: 'pcy-dropdownlist' }}
                startYear={2018}
                fyddlValue={
                  searchCondition.reportTargetYear !== null && searchCondition.reportTargetYear !== ''
                    ? Number(searchCondition.reportTargetYear)
                    : undefined
                }
                fmddlValue={
                  searchCondition.reportTargetMonth !== null && searchCondition.reportTargetMonth !== ''
                    ? Number(searchCondition.reportTargetMonth)
                    : undefined
                }
                onYearChange={(year) => handleSearchCondition('reportTargetYear', year.toString().padStart(2, '0'))}
                onMonthChange={(month) => handleSearchCondition('reportTargetMonth', month.toString().padStart(2, '0'))}
              />
            </GridLayoutItem>
            <GridLayoutItem row={1} col={4}>
              <Label style={{ fontSize: '0.9em' }}>出力年数 (山積管理表)</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={5} colSpan={2}>
              <OutputYearsDropDownList
                popupSettings={{ className: 'pcy-dropdownlist' }}
                onChange={(value) => handleSearchCondition('reportOutputYears', value)}
                value={
                  searchCondition.reportOutputYears !== null
                    ? searchCondition.reportOutputYears
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={1} col={7}>
              <Label>データ出力期間</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={8} colSpan={5}>
              <StackLayout orientation='horizontal'>
                <FiscalMonthDropDownList
                  popupSettings={{ className: 'pcy-dropdownlist' }}
                  startYear={2018}
                  fyddlValue={
                    searchCondition.exportTargetYearFrom !== null && searchCondition.exportTargetYearFrom !== ''
                      ? Number(searchCondition.exportTargetYearFrom)
                      : undefined
                  }
                  fmddlValue={
                    searchCondition.exportTargetMonthFrom !== null && searchCondition.exportTargetMonthFrom !== ''
                      ? Number(searchCondition.exportTargetMonthFrom)
                      : undefined
                  }
                  onYearChange={(year: string | number) => handleSearchCondition('exportTargetYearFrom', year !== '' ? year.toString().padStart(2, '0') : '')}
                  onMonthChange={(month: string | number) => handleSearchCondition('exportTargetMonthFrom', month !== '' ? month.toString().padStart(2, '0') : '')}
                  clear={true}
                />
                &nbsp;～&nbsp;
                <FiscalMonthDropDownList
                  popupSettings={{ className: 'pcy-dropdownlist' }}
                  startYear={2018}
                  fyddlValue={
                    searchCondition.exportTargetYearTo !== null && searchCondition.exportTargetYearTo !== ''
                      ? Number(searchCondition.exportTargetYearTo)
                      : undefined
                  }
                  fmddlValue={
                    searchCondition.exportTargetMonthTo !== null && searchCondition.exportTargetMonthTo !== ''
                      ? Number(searchCondition.exportTargetMonthTo)
                      : undefined
                  }
                  onYearChange={(year: string | number) => handleSearchCondition('exportTargetYearTo', year !== '' ? year.toString().padStart(2, '0') : '')}
                  onMonthChange={(month: string | number) => handleSearchCondition('exportTargetMonthTo', month !== '' ? month.toString().padStart(2, '0') : '')}
                  clear={true}
                />
              </StackLayout>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={1}>
              <Label>帳票フォーマット</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={2} colSpan={2}>
              <DepartmentDropDownList
                popupSettings={{ className: 'pcy-dropdownlist' }}
                onChange={(value) => handleSearchCondition('reportFormat', value)}
                value={
                  searchCondition.reportFormat !== null
                    ? searchCondition.reportFormat
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={2} col={4}>
              <Label>支店*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={5} colSpan={2}>
              <BranchSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                onChange={(valueList) => handleSearchCondition('branchCd', valueList)}
                defaultValue={
                  searchCondition.branchCd !== null && searchCondition.branchCd.length !== 0
                    ? searchCondition.branchCd
                    : undefined
                }
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={2} col={7}>
              <Label style={{ fontSize: '0.9em' }}>比較対象支店 (消化高)</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={8} colSpan={2}>
              <BranchSelect
                value={[searchCondition.compareBranchCd ?? '']}
                data={searchCondition.branchCd ?? []}
                onChange={(valueList) => handleSearchCondition('compareBranchCd', valueList[0])}
                clear={true}
              />
            </GridLayoutItem>
            <GridLayoutItem row={2} col={10}>
              <Label>費目*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={11} colSpan={2}>
              <ExpenseSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                onChange={(valueList) => handleSearchCondition('expense', valueList)}
                value={
                  searchCondition.expense !== null && searchCondition.expense.length !== 0
                    ? searchCondition.expense
                    : undefined
                }
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={1}>
              <Label>調達番号*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={2} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('procurementNo', e.value)}
                value={
                  searchCondition.procurementNo !== null
                    ? searchCondition.procurementNo
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={4}>
              <Label>物件番号*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={5} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('buildingNo', e.value)}
                value={
                  searchCondition.buildingNo !== null
                    ? searchCondition.buildingNo
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={7}>
              <Label>発注者名*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={8} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('customerNm', e.value)}
                value={
                  searchCondition.customerNm !== null
                    ? searchCondition.customerNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={10}>
              <Label>選択と集中*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={11} colSpan={2}>
              <AvailabilityStatusSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.choiceAndConcentrationTarget !== null && searchCondition.choiceAndConcentrationTarget.length !== 0
                    ? searchCondition.choiceAndConcentrationTarget.map((item) => { return String(item) })
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('choiceAndConcentrationTarget', valueList.filter((item) => item !== '').map((item) => { return Number(item) }))}
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={1}>
              <Label>備考</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={2} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('remark', e.value)}
                value={
                  searchCondition.remark !== null
                    ? searchCondition.remark
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={4}>
              <Label>入手年度</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={5} colSpan={2}>
              <FiscalYearDropDownList
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                origin={-5}
                range={16}
                fyddlValueList={
                  searchCondition.obtainFy !== null && searchCondition.obtainFy.length !== 0
                    ? searchCondition.obtainFy
                    : undefined
                }
                onYearChangeList={(valueList) => handleSearchCondition('obtainFy', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={7}>
              <Label>入手経緯</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={8} colSpan={2}>
              <ObtainHistorySelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.obtainHistoryCd !== null && searchCondition.obtainHistoryCd.length !== 0
                    ? searchCondition.obtainHistoryCd
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('obtainHistoryCd', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={10}>
              <Label>施工形式</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={11} colSpan={2}>
              <ConstrFormSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.constructionFormCd !== null && searchCondition.constructionFormCd.length !== 0
                    ? searchCondition.constructionFormCd
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('constructionFormCd', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={5} col={1}>
              <Label>評価</Label>
            </GridLayoutItem>
            <GridLayoutItem row={5} col={2} colSpan={2}>
              <EvaluationSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.evaluationValueCd !== null && searchCondition.evaluationValueCd.length !== 0
                    ? searchCondition.evaluationValueCd
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('evaluationValueCd', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={5} col={4}>
              <Label>取組レベル</Label>
            </GridLayoutItem>
            <GridLayoutItem row={5} col={5} colSpan={2}>
              <EffortLevelSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.effortLevelCd !== null && searchCondition.effortLevelCd.length !== 0
                    ? searchCondition.effortLevelCd
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('effortLevelCd', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={5} col={7}>
              <Label>調達状況</Label>
            </GridLayoutItem>
            <GridLayoutItem row={5} col={8} colSpan={2}>
              <ProcurementStatusSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.procurementSts !== null && searchCondition.procurementSts.length !== 0
                    ? searchCondition.procurementSts
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('procurementSts', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={5} col={10}>
              <Label>山積対象フラグ*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={5} col={11} colSpan={2}>
              <NormalStatusSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.yamadumiFlg !== null && searchCondition.yamadumiFlg.length !== 0
                    ? searchCondition.yamadumiFlg.map((item) => { return String(item) })
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('yamadumiFlg', valueList.map((item) => { return Number(item) }))}
              />
            </GridLayoutItem>
            <GridLayoutItem row={6} col={1}>
              <Label>建材部公開フラグ</Label>
            </GridLayoutItem>
            <GridLayoutItem row={6} col={2} colSpan={2}>
              <NormalStatusSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.kenzaiDiscloseFlg !== null && searchCondition.kenzaiDiscloseFlg.length !== 0
                    ? searchCondition.kenzaiDiscloseFlg.map((item) => { return String(item) })
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('kenzaiDiscloseFlg', valueList.map((item) => { return Number(item) }))}
              />
            </GridLayoutItem>
            <GridLayoutItem row={6} col={4}>
              <Label>業者公開フラグ</Label>
            </GridLayoutItem>
            <GridLayoutItem row={6} col={5} colSpan={2}>
              <NormalStatusSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.supplierDiscloseFlg !== null && searchCondition.supplierDiscloseFlg.length !== 0
                    ? searchCondition.supplierDiscloseFlg.map((item) => { return String(item) })
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('supplierDiscloseFlg', valueList.map((item) => { return Number(item) }))}
              />
            </GridLayoutItem>
            <GridLayoutItem row={6} col={7}>
              <Label>工事名*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={6} col={8} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('constructionNm', e.value)}
                value={
                  searchCondition.constructionNm !== null
                    ? searchCondition.constructionNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={6} col={10}>
              <Label>工事科目コード*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={6} col={11} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('constructionSubjectCd', e.value)}
                value={
                  searchCondition.constructionSubjectCd !== null
                    ? searchCondition.constructionSubjectCd
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={7} col={1}>
              <Label>総工事価格 (百万)*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={7} col={2} colSpan={2}>
              <FromToNumberInput
                onLimitLeftChange={(value) => handleSearchCondition('totalCostFrom', value)}
                onLimitRightChange={(value) => handleSearchCondition('totalCostTo', value)}
                limitLeftValue={searchCondition.totalCostFrom ?? undefined}
                limitRightValue={searchCondition.totalCostTo ?? undefined}
              />
            </GridLayoutItem>
            <GridLayoutItem row={7} col={4}>
              <Label style={{ fontSize: '0.8em' }}>当社概算工事価格 (百万)</Label>
            </GridLayoutItem>
            <GridLayoutItem row={7} col={5} colSpan={2}>
            <FromToNumberInput
                onLimitLeftChange={(value) => handleSearchCondition('ourCompanyTotalCostFrom', value)}
                onLimitRightChange={(value) => handleSearchCondition('ourCompanyTotalCostTo', value)}
                limitLeftValue={searchCondition.ourCompanyTotalCostFrom ?? undefined}
                limitRightValue={searchCondition.ourCompanyTotalCostTo ?? undefined}
              />
            </GridLayoutItem>
            <GridLayoutItem row={7} col={7}>
              <Label>工期*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={7} col={8} colSpan={5}>
              <StackLayout orientation='horizontal'>
                <FiscalMonthDropDownList
                  popupSettings={{ className: 'pcy-dropdownlist' }}
                  origin={-3}
                  range={14}
                  fyddlValue={
                    searchCondition.startY !== null && searchCondition.startY !== ''
                      ? Number(searchCondition.startY)
                      : undefined
                  }
                  fmddlValue={
                    searchCondition.startM !== null && searchCondition.startM !== ''
                      ? Number(searchCondition.startM)
                      : undefined
                  }
                  onYearChange={(year: string | number) => handleSearchCondition('startY', year !== '' ? year.toString().padStart(2, '0') : '')}
                  onMonthChange={(month: string | number) => handleSearchCondition('startM', month !== '' ? month.toString().padStart(2, '0') : '')}
                  clear={true}
                />
                &nbsp;～&nbsp;
                <FiscalMonthDropDownList
                  popupSettings={{ className: 'pcy-dropdownlist' }}
                  origin={-3}
                  range={24}
                  fyddlValue={
                    searchCondition.completionY !== null && searchCondition.completionY !== ''
                      ? Number(searchCondition.completionY)
                      : undefined
                  }
                  fmddlValue={
                    searchCondition.completionM !== null && searchCondition.completionM !== ''
                      ? Number(searchCondition.completionM)
                      : undefined
                  }
                  onYearChange={(year: string | number) => handleSearchCondition('completionY', year !== '' ? year.toString().padStart(2, '0') : '')}
                  onMonthChange={(month: string | number) => handleSearchCondition('completionM', month !== '' ? month.toString().padStart(2, '0') : '')}
                  clear={true}
                />
              </StackLayout>
            </GridLayoutItem>

            <GridLayoutItem row={8} col={1}>
              <Label>工事種別</Label>
            </GridLayoutItem>
            <GridLayoutItem row={8} col={2} colSpan={2}>
              <ConstrTypeSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.constructionCd !== null && searchCondition.constructionCd.length !== 0
                    ? searchCondition.constructionCd
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('constructionCd', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={8} col={4}>
              <Label>建物用途*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={8} col={5} colSpan={2}>
              <BuildingUsecaseSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.architectureUse !== null && searchCondition.architectureUse.length !== 0
                    ? searchCondition.architectureUse
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('architectureUse', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={8} col={7}>
              <Label>構造-地上*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={8} col={8} colSpan={2}>
              <StructureTypeSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.structureGround !== null && searchCondition.structureGround.length !== 0
                    ? searchCondition.structureGround
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('structureGround', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={8} col={10}>
              <Label>構造-地下*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={8} col={11} colSpan={2}>
              <StructureTypeSelect
                popupSettings={{ className: 'pcy-dropdownlist' }}
                multiple
                value={
                  searchCondition.structureUnderground !== null && searchCondition.structureUnderground.length !== 0
                    ? searchCondition.structureUnderground
                    : undefined
                }
                onChange={(valueList) => handleSearchCondition('structureUnderground', valueList)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={9} col={1}>
              <Label>特殊工法</Label>
            </GridLayoutItem>
            <GridLayoutItem row={9} col={2} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('specialConstructionMethod', e.value)}
                value={
                  searchCondition.specialConstructionMethod !== null
                    ? searchCondition.specialConstructionMethod
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={9} col={4}>
              <Label>入手年月</Label>
            </GridLayoutItem>
            <GridLayoutItem row={9} col={5} colSpan={2}>
              <FiscalMonthDropDownList
                popupSettings={{ className: 'pcy-dropdownlist' }}
                origin={-5}
                range={16}
                fyddlValue={
                  searchCondition.obtainY !== null && searchCondition.obtainY !== ''
                    ? Number(searchCondition.obtainY)
                    : undefined
                }
                fmddlValue={
                  searchCondition.obtainM !== null && searchCondition.obtainM !== ''
                    ? Number(searchCondition.obtainM)
                    : undefined
                }
                onYearChange={(year: string | number) => handleSearchCondition('obtainY', year !== '' ? year.toString().padStart(2, '0') : '')}
                onMonthChange={(month: string | number) => handleSearchCondition('obtainM', month !== '' ? month.toString().padStart(2, '0') : '')}
                clear={true}
              />
            </GridLayoutItem>
            <GridLayoutItem row={9} col={7}>
              <Label>工事部名</Label>
            </GridLayoutItem>
            <GridLayoutItem row={9} col={8} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('constructionSectionNm', e.value)}
                value={
                  searchCondition.constructionSectionNm !== null
                    ? searchCondition.constructionSectionNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={9} col={10}>
              <Label>所長担当者名*</Label>
            </GridLayoutItem>
            <GridLayoutItem row={9} col={11} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('chiefPersonNm', e.value)}
                value={
                  searchCondition.chiefPersonNm !== null
                    ? searchCondition.chiefPersonNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={10} col={1}>
              <Label>本社営業部名</Label>
            </GridLayoutItem>
            <GridLayoutItem row={10} col={2} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('hqSalesSectionNm', e.value)}
                value={
                  searchCondition.hqSalesSectionNm !== null
                    ? searchCondition.hqSalesSectionNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={10} col={4}>
              <Label>本社営業担当者名</Label>
            </GridLayoutItem>
            <GridLayoutItem row={10} col={5} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('hqSalesPersonNm', e.value)}
                value={
                  searchCondition.hqSalesPersonNm !== null
                    ? searchCondition.hqSalesPersonNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={10} col={7}>
              <Label>支店営業部名</Label>
            </GridLayoutItem>
            <GridLayoutItem row={10} col={8} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('branchSalesSectionNm', e.value)}
                value={
                  searchCondition.branchSalesSectionNm !== null
                    ? searchCondition.branchSalesSectionNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={10} col={10}>
              <Label>支店営業担当者名</Label>
            </GridLayoutItem>
            <GridLayoutItem row={10} col={11} colSpan={2}>
              <Input
                onChange={(e) => handleSearchCondition('branchSalesPersonNm', e.value)}
                value={
                  searchCondition.branchSalesPersonNm !== null
                    ? searchCondition.branchSalesPersonNm
                    : ''
                }
              />
            </GridLayoutItem>
            <GridLayoutItem row={14} col={1} colSpan={6}>
              <Label>※ 汎用検索は「CSV出力」にのみ適用されます</Label>
            </GridLayoutItem>
            <GridLayoutItem row={15} col={1}>
              <Label>汎用検索１</Label>
            </GridLayoutItem>
            <GridLayoutItem row={15} col={2} colSpan={5}>
            <GeneralPurposeSearch
                searchItem={
                  generalPurposeSearchList[0].searchItemCd !== null && generalPurposeSearchList[0].searchItemCd !== ''
                    ? generalPurposeSearchList[0].searchItemCd
                    : undefined
                }
                searchValue={
                  generalPurposeSearchList[0].searchValue !== null
                    ? generalPurposeSearchList[0].searchValue
                    : ''
                }
                searchMethod={
                  generalPurposeSearchList[0].searchMethodCd !== null && generalPurposeSearchList[0].searchMethodCd !== ''
                    ? generalPurposeSearchList[0].searchMethodCd
                    : undefined
                }
                onSearchItemChange={(valueList) => handleGeneralPurposeSearch(0, 'searchItemCd', valueList[0])}
                onSearchValueChange={(value) => handleGeneralPurposeSearch(0, 'searchValue', value)}
                onSearchMethodChange={(valueList) => handleGeneralPurposeSearch(0, 'searchMethodCd', valueList[0])}
                clear={true}
              />
            </GridLayoutItem>
            <GridLayoutItem row={15} col={7}>
              <Label>汎用検索２</Label>
            </GridLayoutItem>
            <GridLayoutItem row={15} col={8} colSpan={5}>
              <GeneralPurposeSearch
                searchItem={
                  generalPurposeSearchList[1].searchItemCd !== null && generalPurposeSearchList[1].searchItemCd !== ''
                    ? generalPurposeSearchList[1].searchItemCd
                    : undefined
                }
                searchValue={
                  generalPurposeSearchList[1].searchValue !== null
                    ? generalPurposeSearchList[1].searchValue
                    : ''
                }
                searchMethod={
                  generalPurposeSearchList[1].searchMethodCd !== null && generalPurposeSearchList[1].searchMethodCd !== ''
                    ? generalPurposeSearchList[1].searchMethodCd
                    : undefined
                }
                onSearchItemChange={(valueList) => handleGeneralPurposeSearch(1, 'searchItemCd', valueList[0])}
                onSearchValueChange={(value) => handleGeneralPurposeSearch(1, 'searchValue', value)}
                onSearchMethodChange={(valueList) => handleGeneralPurposeSearch(1, 'searchMethodCd', valueList[0])}
                clear={true}
              />
            </GridLayoutItem>
            <GridLayoutItem row={16} col={1}>
              <Label>汎用検索３</Label>
            </GridLayoutItem>
            <GridLayoutItem row={16} col={2} colSpan={5}>
            <GeneralPurposeSearch
                searchItem={
                  generalPurposeSearchList[2].searchItemCd !== null && generalPurposeSearchList[2].searchItemCd !== ''
                    ? generalPurposeSearchList[2].searchItemCd
                    : undefined
                }
                searchValue={
                  generalPurposeSearchList[2].searchValue !== null
                    ? generalPurposeSearchList[2].searchValue
                    : ''
                }
                searchMethod={
                  generalPurposeSearchList[2].searchMethodCd !== null && generalPurposeSearchList[2].searchMethodCd !== ''
                    ? generalPurposeSearchList[2].searchMethodCd
                    : undefined
                }
                onSearchItemChange={(valueList) => handleGeneralPurposeSearch(2, 'searchItemCd', valueList[0])}
                onSearchValueChange={(value) => handleGeneralPurposeSearch(2, 'searchValue', value)}
                onSearchMethodChange={(valueList) => handleGeneralPurposeSearch(2, 'searchMethodCd', valueList[0])}
                clear={true}
              />
            </GridLayoutItem>
            <GridLayoutItem row={16} col={7}>
              <Label>汎用検索４</Label>
            </GridLayoutItem>
            <GridLayoutItem row={16} col={8} colSpan={5}>
            <GeneralPurposeSearch
                searchItem={
                  generalPurposeSearchList[3].searchItemCd !== null && generalPurposeSearchList[3].searchItemCd !== ''
                    ? generalPurposeSearchList[3].searchItemCd
                    : undefined
                }
                searchValue={
                  generalPurposeSearchList[3].searchValue !== null
                    ? generalPurposeSearchList[3].searchValue
                    : ''
                }
                searchMethod={
                  generalPurposeSearchList[3].searchMethodCd !== null && generalPurposeSearchList[3].searchMethodCd !== ''
                    ? generalPurposeSearchList[3].searchMethodCd
                    : undefined
                }
                onSearchItemChange={(valueList) => handleGeneralPurposeSearch(3, 'searchItemCd', valueList[0])}
                onSearchValueChange={(value) => handleGeneralPurposeSearch(3, 'searchValue', value)}
                onSearchMethodChange={(valueList) => handleGeneralPurposeSearch(3, 'searchMethodCd', valueList[0])}
                clear={true}
              />
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
        </div>
      </Card>
      <div className="pcy-search-body-pane-footer pcy-control-box" style={{ width: '90%', margin: 'auto', backgroundColor: 'white' }}>
        <StackLayout gap={10} orientation='vertical'>
          <StackLayout gap={5} align={{ horizontal: 'end' }}>
            <Label>抽出条件/出力対象</Label>
            <Button
              onClick={handleClear}
            >
              クリア
            </Button>
            <Button
              onClick={handleRegistCondition}
              themeColor="primary"
            >
              条件登録
            </Button>
            <Button
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleConditionImport}
              themeColor="primary"
            >
              条件取込
            </Button>
            <Button
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleConditionExport}
              themeColor="primary"
            >
              条件出力
            </Button>
          </StackLayout>
          <StackLayout orientation='horizontal'>
            <StackLayout gap={5} align={{ horizontal: 'start' }}>
              <Button
                onClick={handleSelect}
                themeColor="primary"
              >
                {isAllSelectMode ? '全選択' : '全解除'}
              </Button>
              <Button
                onClick={handleItemSort}
                themeColor="primary"
              >
                並べ替え
              </Button>
            </StackLayout>
            <StackLayout gap={5} align={{ horizontal: 'end' }}>
              <Button
                onClick={handleYamadumiSimulationOutput}
                themeColor="primary"
              >
                山積シミュレーション出力
              </Button>
              <Button
                onClick={handleSyoukatakaSimulationOutput}
                themeColor="primary"
              >
                消化高シミュレーション出力
              </Button>
              <Button
                onClick={handleYamadumiManagementOutput}
                themeColor="primary"
              >
                山積管理表出力
              </Button>
            </StackLayout>
          </StackLayout>
        </StackLayout>
      </div>
      <Grid
        style={{ width: '90%', height: '55%', margin: '5px auto' }}
        sortable={false}
        filterable={false}
        scrollable='scrollable'
        data={outputItemList}
        dataItemKey='outputItemCd'
        editField='inEdit'
        onItemChange={handleOutputItemData}
      >
        <GridColumn
          title="出力対象"
          field="check"
          width="100px"
          cell={(props: GridCellProps) => (
            <td>
              <Checkbox
                checked={props.dataItem.outputCheck}
                onChange={(e) => handleOutputItemList(props.dataIndex, 'outputCheck', e.value)}
              />
            </td>
          )}
        />
        <GridColumn
          title="CSV出力順（列）"
          field="outputOrder"
          width="125px"
        />
        <GridColumn
          title="データソート順（行）"
          field="sortOrder"
          width="150px"
        />
        <GridColumn
          title="順序"
          field="sortMethod"
          width="100px"
          cell={(props: GridCellProps) => (
            <td>
              <SortMethodDropDownList
                style={{ height: '25px' }}
                value={props.dataItem.sortMethod}
                onChange={(value) => handleOutputItemList(props.dataIndex, 'sortMethod', value)}
              />
            </td>
          )}
        />
        <GridColumn
          title="項目名"
          field="outputItemDispNmWithAnnotation"
          editable={false}
        />
      </Grid>
      <div className="pcy-search-body-pane-footer pcy-control-box" style={{ width: '90%', margin: 'auto', backgroundColor: 'white' }}>
        <StackLayout gap={5} align={{ horizontal: 'end' }}>
          <Button
            onClick={() => { navigate(-1) }}
          >
            戻る
          </Button>
          <Button
            onClick={handleTableauGraph}
            themeColor="primary"
          >
            消化高・労務集計グラフ
          </Button>
          <Button
            onClick={handleTableauGraphSupplier}
            themeColor="primary"
          >
            業者山積グラフ
          </Button>
          <Button
            onClick={handleCsvDataOutput}
            themeColor="primary"
          >
            CSV出力
          </Button>
          <DropDownButton
            text="顔認証出力"
            items={['入退場データ', '入場データ']}
            themeColor="primary"
            onItemClick={(e) => handleFaceRecognitionCsvDataOutput(e.itemIndex)}
          />
        </StackLayout>
      </div>
      <ConfirmDialog
        open={confirmDialogShow}
        id={messageId}
        renderCloseButton
        title={'確認'}
        args={messageArgslist}
        onClickOk={dialogComfirmOkBtn}
        onClickCancel={() => setConfirmDialogShow(false)}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </div>
  )
}
